import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { ElasticGlobalSearchResponse, SearchResults } from 'app/pages/search-result/models/search-result.model';
import { LanguageService } from 'app/shared/services/language.service';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DataService } from '../../shared/services/data.service';
import { SEARCH_CONSTANTS } from '../constants/search.constants';
import { Router } from '@angular/router';
import { NavigationStateService } from './navigation-state-service';

export interface SearchGenericResults<T> {
  total: number;
  values: T[];
}

@Injectable({
  providedIn: 'root'
})
export class ElasticsearchService {
  elasticsearchUrl = environment.elasticsearch.elasticsearchUrl;
  readonly dataService = inject(DataService);
  readonly currentLanguage = inject(LanguageService).currentLanguage;
  private readonly injector = inject(Injector);
  private router = inject(Router);

  query: any = this.createBaseQueryWithBoosts();
  constructor(private navigationStateService: NavigationStateService) {}

  initQuery(applyTypeKeywordBoosts: boolean = false, isAutoComplete: boolean = false): this {
    this.query = this.createBaseQueryWithBoosts(applyTypeKeywordBoosts, isAutoComplete);
    (this.query as any)._applyTypeKeywordBoosts = applyTypeKeywordBoosts;
    return this;
  }

  executeSearch<T>(
    indexes: string[],
    isAutoComplete?: boolean,
    sortField?: string,
    skipLoader?: boolean,
    totalResult?: number,
  ): Observable<SearchGenericResults<T>> {
    // Apply function_score query if needed (after all queries are added)
    if ((this.query as any)._applyTypeKeywordBoosts) {
      this.appendFunctionScoreQuery(this.query, isAutoComplete);
    } else {
      // Build search query even without function_score
      this.appendSearchQuery(this.query, isAutoComplete);
    }
    // Clean up internal fields before sending
    const cleanQuery = { ...this.query };
    delete (cleanQuery as any)._applyTypeKeywordBoosts;
    delete (cleanQuery as any)._searchTerm;
    delete (cleanQuery as any)._searchFields;
    const excludedIndexes = ['he_phone_index', 'en_phone_index'];
    const indexPath =
    indexes.length > 0
      ? `/${indexes.join(',')}`
      : `/*,-${excludedIndexes.join(',-')}`;    
    const url = `${this.elasticsearchUrl}${indexPath}/_search`;
    return this.dataService
      .post<T>(url, cleanQuery, this.getHeaders(), skipLoader)
      .pipe(map((data) => this.mapResults<T>(data, sortField, totalResult)));
  }

  searchAllFieldsByTerm(term?: string, fields?: string[]): this {
    if (!term) {
      return this;
    }

    const searchFields = fields && fields.length > 0 ? fields : ['*'];

    // Store the search term and fields for function_score query
    (this.query as any)._searchTerm = this.escapeElasticsearchSpecialChars(term);
    (this.query as any)._searchFields = searchFields;

    return this;
  }

  private escapeElasticsearchSpecialChars(text: string): string {
    return text.replace(/([+\-=&|><!(){}[\]^"~*?:\\/])/g, '\\$1');
  }

  searchAllFieldsByPartialTerm(term?: string, fields?: string[]): this {
    return this.searchAllFieldsByTerm(term, fields);
  }

  searchAll(from: number = 0, size: number = 1000): this {
    this.query.query = { match_all: {} };
    this.query.from = from;
    this.query.size = size;
    return this;
  }

  filterByField(field?: string, value?: string): this {
    if (field && value) {
      const boolQuery = this.getBoolQuery();
      boolQuery.must.push({
        match_phrase: {
          [field]: value
        }
      });
    }
    return this;
  }

  filterByFieldValues(field?: string, values?: string | (string | undefined)[]): this {
    if (!field || !values) return this;

    const valueArray = Array.isArray(values) ? values : [values];
    const validValues = valueArray.filter((v): v is string => !!v);

    if (validValues.length > 0) {
      const shouldArray = validValues.map((value) => ({
        match_phrase: {
          [field]: value
        }
      }));

      const boolQuery = this.getBoolQuery();
      boolQuery.must.push({
        bool: {
          should: shouldArray,
          minimum_should_match: 1
        }
      });
    }

    return this;
  }

  searchAllFieldsByValues(values?: string | (string | undefined)[], fields?: string[]): this {
    if (!values) return this;

    const valueArray = Array.isArray(values) ? values : [values];
    const validValues = valueArray.filter((v): v is string => !!v);
    const searchFields = fields && fields.length > 0 ? fields : ['*'];

    if (validValues.length > 0) {
      const boolQuery = this.getBoolQuery();
      boolQuery.filter.push({
        terms: {
          'typeText.keyword': validValues
        }
      });
    }

    return this;
  }

  filterByDateAfter(field: string, date: Date = new Date()): this {
    if (!field) return this;

    const isoDate = date.toISOString();
    const boolQuery = this.getBoolQuery();

    boolQuery.must.push({
      range: {
        [field]: {
          gte: isoDate
        }
      }
    });

    return this;
  }

  addPagination(skip?: number, take?: number): this {
    this.query = {
      ...this.query,
      from: skip ?? 0,
      size: take ?? 10000
    };
    return this;
  }

  private getBoolQuery(): any {
    // Always work with the regular bool query during query building
    // The function_score will wrap it later in executeSearch
    if (!this.query.query.bool) {
      this.query.query.bool = {
        filter: [],
        must: [],
        should: [],
        minimum_should_match: 0
      };
    }
    return this.query.query.bool;
  }

  private createBaseQueryWithBoosts(applyTypeKeywordBoosts: boolean = false, isAutoComplete: boolean = false) {
    const baseQuery: any = {
      from: 0,
      size: 10000,
      query: {
        bool: {
          filter: [] as unknown[],
          must: [] as unknown[],
          should: [] as unknown[],
          minimum_should_match: 0
        }
      }
    };
    if (isAutoComplete)
      baseQuery._source = ["title", "link"];


    return baseQuery;
  }

  private appendSearchQuery(query: any, isAutoComplete?: boolean): void {
    const searchTerm = (query as any)._searchTerm;
    const searchFields = (query as any)._searchFields || ['*'];

    if (searchTerm) {
      const boolQuery = this.getBoolQuery();

      const shouldClauses: any[] = [];
      shouldClauses.push({
        wildcard: {
          "title.keyword": {
            value: '*' + searchTerm + '*',
            boost: SEARCH_CONSTANTS.WILDCARD_TITLE_BOOST,
          }
        }
      });
      shouldClauses.push({
        match_phrase: {
          title: {
            query: searchTerm,
            boost: SEARCH_CONSTANTS.TITLE_MATCH_BOOST,
            slop: 1
          }
        }
      });
      if (!isAutoComplete) {
        shouldClauses.push({
          multi_match: {
            query: searchTerm,
            type: "phrase",
            fields: searchFields,
            operator: 'and'
          }
        });
      }

      boolQuery.must.push({
        bool: {
          should: shouldClauses,
          minimum_should_match: 1
        }
      });
    }
  }
  getHospitalParamFromGlobalSearchPage(): string | null {
    const url = window.location.href;

    const parsedUrl = new URL(url);
    const hospitalParam = parsedUrl.searchParams.get('hospital'); // "he/tel-hashomer/"

    let lastPart: string | null = null;

    if (hospitalParam) {
      const decoded = decodeURIComponent(hospitalParam); // "he/tel-hashomer/"
      lastPart = decoded.split('/').filter(Boolean).pop() || null;
    }
    return lastPart;
  }
  private appendFunctionScoreQuery(query: any, isAutoComplete?: boolean): void {
    const typeKeywordBoosts = SEARCH_CONSTANTS.TYPE_KEYWORD_BOOSTS;
    const searchTerm = (query as any)._searchTerm;
    const searchFields = (query as any)._searchFields || ['*'];

    // Get existing must and filter clauses from the bool query
    const existingMust = query.query.bool?.must || [];
    const existingFilter = query.query.bool?.filter || [];

    // Build the bool query with match and multi_match
    const boolQuery: any = {
      bool: {
        must: [...existingMust]
      }
    };



    if (isAutoComplete) {
      const must: any = [];

      this.navigationStateService.hasVisitedHospitalLobby().subscribe((visited) => {
        if (visited) {
          let hospitalName = this.getHospitalParamFromGlobalSearchPage();
          if (!hospitalName){
            const parts = this.router.url.split('/');
            hospitalName = parts[1] == this.currentLanguage().prefix? parts[2]: parts[1];
          }
          if (hospitalName && !hospitalName.includes('?') && hospitalName != this.currentLanguage().prefix && !hospitalName.endsWith('/home')) {
            must.push({
              match_phrase: {
                hospital: hospitalName // שימוש בשם בית החולים רק אם קיים
              }
            });
          }
        }
      });

      const safeSearchTerm = searchTerm.trim().replace(/\s+/g, ' ');

      must.push({
        query_string: {
          default_field: "title",
          query: '*' + safeSearchTerm + '*',

        }
      });

      boolQuery.bool.must = must;
    }
    else {
      // Only add filter if it has items
      if (existingFilter.length > 0) {
        boolQuery.bool.filter = [...existingFilter];
      }

      if (searchTerm) {
        const shouldClauses = [
          {
            wildcard: {
              "title.keyword": {
                value: '*' + searchTerm + '*',
                boost: SEARCH_CONSTANTS.WILDCARD_TITLE_BOOST,
              }
            }
          },
          {
            match_phrase: {
              title: {
                query: searchTerm,
                boost: SEARCH_CONSTANTS.TITLE_MATCH_BOOST,
                slop: 1
              }
            }
          },
          {
            multi_match: {
              query: searchTerm,
              fields: searchFields,
              type: "phrase",
              operator: "and"
            }
          }
        ];


        boolQuery.bool.must.push({
          bool: {
            should: shouldClauses,
            minimum_should_match: 1
          }
        });
      }

      // Build functions from TYPE_KEYWORD_BOOSTS
      const functions = typeKeywordBoosts
        .filter((boostConfig) => boostConfig?.name)
        .map((boostConfig) => ({
          filter: {
            term: {
              'typeText.keyword': boostConfig.name.trim()
            }
          },
          weight: boostConfig.boost ?? 1
        }));
    }
    // Create function_score query
    query.query = {
      function_score: {
        query: boolQuery,
        // functions: functions,
        score_mode: 'sum',
        boost_mode: 'sum'
      }
    };

  }

  private getHeaders() {
    const username = environment.elasticsearch.elasticsearcUserName;
    const password = environment.elasticsearch.elasticsearchPassword;
    const credentials = `${username}:${password}`;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(username + ':' + password)
    });
  }

  private mapResults<T>(data: any, sortField?: string, totalResult?: number): SearchGenericResults<T> {
    let sorted: any[] = [];

    if (sortField) {
      sorted = this.sortResultsByScoreAndField<T>(data?.hits?.hits ?? [], sortField);
    } else {
      sorted = data?.hits?.hits ?? [];
    }
    const mappedValues = sorted.map((v: any) => v._source as T);
    const limitedValues =
      totalResult && mappedValues.length > totalResult ? mappedValues.slice(0, totalResult) : mappedValues;

    return {
      values: limitedValues,
      total: totalResult
        ? data?.hits?.total?.value < (totalResult ?? 100)
          ? data?.hits?.total?.value
          : totalResult ?? data?.hits?.total?.value
        : data?.hits?.total?.value
    } as SearchGenericResults<T>;
  }

  private sortResultsByScoreAndField<T>(hits: any[], sortField: string): any[] {
    return hits.sort((a: any, b: any) => {
      const scoreDiff = (b._score ?? 0) - (a._score ?? 0);
      if (scoreDiff !== 0) return scoreDiff;

      const valueA = a._source?.[sortField];
      const valueB = b._source?.[sortField];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return 1;
      if (valueB == null) return -1;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueA - valueB;
      }

      if (this.isValidDate(valueA) && this.isValidDate(valueB)) {
        return new Date(valueA).getTime() - new Date(valueB).getTime();
      }

      return String(valueA).localeCompare(String(valueB), this.currentLanguage().isoCode);
    });
  }

  private isValidDate(value: any): boolean {
    if (typeof value !== 'string') return false;

    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
    if (!isoDateRegex.test(value)) return false;

    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  getAutocompleteSuggestions(
    term: string,
    indexName: string,
    fields?: string[],
    filters?: { [key: string]: any }
  ): Observable<SearchResults> {
    const queryBuilder = this.initQuery(undefined, true);

    if (filters) {
      if (filters['typeId']) {
        queryBuilder.filterByFieldValues('typeId', filters['typeId']);
      }

      if ('endDate' in filters) {
        queryBuilder.filterByDateAfter('endDate');
      }

      Object.keys(filters).forEach((key) => {
        if (key !== 'typeId' && key !== 'endDate' && filters[key] !== undefined && filters[key] !== null) {
          if (Array.isArray(filters[key])) {
            queryBuilder.filterByFieldValues(key, filters[key]);
          } else {
            queryBuilder.filterByField(key, filters[key].toString());
          }
        }
      });
    }

    const query = queryBuilder.searchAllFieldsByPartialTerm(term, fields).addPagination(0, 5);

    return query.executeSearch<ElasticGlobalSearchResponse>([`${this.currentLanguage()?.prefix}_${indexName}`], undefined, undefined, true);
  }

  getGlobalSearchIndexes(): string[] {
    const currentLanguagePrefix = this.currentLanguage()?.prefix;

    return [
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.doctor}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.internalDepartment}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.contentPage}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.event}`,
      `${currentLanguagePrefix}_${environment.elasticsearch.indexes.generalContent}`
    ];
  }

  getMultiIndexAutocompleteSuggestions(
    term: string,
    fields?: string[],
    filters?: { [key: string]: any }
  ): Observable<SearchResults> {
    const query = this.initQuery(true, true).searchAllFieldsByTerm(term, fields);

    if (filters) {
      if (filters['typeId']) {
        query.filterByFieldValues('typeId', filters['typeId']);
      }

      if ('endDate' in filters) {
        query.filterByDateAfter('endDate');
      }

      Object.keys(filters).forEach((key) => {
        if (key !== 'typeId' && key !== 'endDate' && filters[key] !== undefined && filters[key] !== null) {
          if (Array.isArray(filters[key])) {
            query.filterByFieldValues(key, filters[key]);
          } else {
            query.filterByField(key, filters[key].toString());
          }
        }
      });
    }

    query.addPagination(0, 5);
    // Pass [] so executeSearch will build /_search (all indexes)
    return query.executeSearch([], true, undefined, true).pipe(
      map((result: SearchGenericResults<any>) => {
        return {
          total: result.total || 0,
          values: result.values?.slice(0, 5) || []
        } as SearchResults;
      })
    );

  }
}
